import { useMemo, useState } from 'react';
import { useMessengerView } from '@/api/views';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { buildConversationsFeed } from '@/utils/conversationsFeed';
import ConversationThread from '@/components/ConversationThread';
import { seedInboundDm } from '@/domain/inboundDm';
import { hookchatDevHarnessEnabled } from '@/config/featureFlags';
import { simulateStripeWebhookPaid, simulateDepositLinkViewed } from '@/domain/depositFlow';
import { selfBookLinkForCustomer, pendingDepositForCustomer, customerReplyEntry } from '@/domain/customerSim';
import type { StudioId } from '@/domain/types';
import { depositEmailFor, emailOnFile } from '@/components/deposits/DepositEmailField';
import { enquiryIntakeIntent } from '@/api/intents';

const CUSTOMER_CHANNELS = new Set(['ig-dm', 'meta-dm', 'sms', 'email']);

/**
 * Customer-side simulator: play the customer against real staging data. Reads
 * the live messenger threads and dispatches customer actions locally — the
 * /console tab's own store + persistence bridge writes them to the DB. Adapts
 * per thread: a customer-scoped self-book link opens the real page; a pending
 * deposit can be viewed/paid (webhook-sim → held booking becomes the booking).
 */
export default function CustomerSimulator() {
  const messenger = useMessengerView();
  const clockIso = useSimulationStore((s) => s.clockIso);
  const customers = useBrainStore((s) => s.customers);
  const selfBookLinks = useBrainStore((s) => s.selfBookLinks);
  const deposits = useBrainStore((s) => s.deposits);
  const studios = useBrainStore((s) => s.studios);
  const addConversationEntry = useBrainStore((s) => s.addConversationEntry);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [newHandle, setNewHandle] = useState('');
  const [newBody, setNewBody] = useState('');
  // tv-isee: "Via HookChat pipeline" posts a signed DM through the real
  // receiver → inbox → worker (dev-only /_dev/hookchat/inbound) instead of
  // seeding it locally; the thread then arrives over /ws like a real DM.
  const [viaHookchat, setViaHookchat] = useState(false);
  const [hoursAgo, setHoursAgo] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const devHarness = hookchatDevHarnessEnabled();
  // D01-02: a website form enquiry, through the server's intake (the staff-only
  // enquiry-intake intent; the real forms send emails, ingested later: Anas 7 Oct),
  // so the web-form intake can be tested on staging without a website.
  const [web, setWeb] = useState({ name: '', mobile: '', email: '', message: '', preferredDate: '' });
  const [webState, setWebState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [webError, setWebError] = useState<string | null>(null);
  // D05-11: paying needs an email; the simulated customer types one when none is on file.
  const [payEmail, setPayEmail] = useState('');

  const feed = useMemo(() => {
    const bundle = messenger.data;
    if (!bundle) return [];
    return buildConversationsFeed({
      customers: bundle.customers,
      // tv-nhs: activityFeed dropped from the messenger view; buildConversationsFeed
      // treats it as an optional no-op, so the missed-call enrichment is off until
      // a (windowed) feed is re-wired here later.
      sequenceState: bundle.sequenceState,
      nowIso: clockIso,
      activeWithinMs: Infinity,
    }).filter((i) => i.customer.conversation.some((entry) => CUSTOMER_CHANNELS.has(entry.channel)));
  }, [messenger.data, clockIso]);

  const selected = selectedId ? messenger.data?.customers[selectedId] ?? customers[selectedId] : undefined;
  const link = selected ? selfBookLinkForCustomer(selfBookLinks, selected.id) : undefined;
  const pending = selected ? pendingDepositForCustomer(deposits, selected) : undefined;
  // Default a new sim DM to Coburg (the demo's home studio), not the store's
  // first key — that order is hydration-dependent and locally resolved to a
  // different studio, seeding DMs invisible in a Coburg-scoped inbox (tv-k9b.13).
  const defaultStudio = ('coburg' in studios ? 'coburg' : Object.keys(studios)[0] ?? 'coburg') as StudioId;
  // A real HookChat thread is server-owned; without the dev harness we must
  // not fabricate a customer reply through the dev-only pipeline route.
  const isHookchatThread = selected?.conversation.some((e) => e.provider === 'hookchat') ?? false;
  const blockHookchatReply = isHookchatThread && !devHarness;

  const postInbound = async (body: Record<string, unknown>): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch('/_dev/hookchat/inbound', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Pipeline refused (${res.status})`);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    if (blockHookchatReply) return;
    // A HookChat thread is server-owned: the customer's reply must come back
    // through the pipeline as their linked identity, not as a local entry.
    if (selected.conversation.some((e) => e.provider === 'hookchat')) {
      if (await postInbound({ customerId: selected.id, text: reply.trim() })) setReply('');
      return;
    }
    const channel = selected.conversation.at(-1)?.channel ?? 'ig-dm';
    addConversationEntry(selected.id, customerReplyEntry(selected.id, channel, reply.trim(), clockIso));
    setReply('');
  };

  const createConversation = async () => {
    if (!newHandle.trim()) return;
    if (viaHookchat) {
      const ok = await postInbound({
        studioId: selected?.primaryStudioId ?? defaultStudio,
        name: newHandle.trim(),
        text: newBody.trim() || undefined,
        hoursAgo: Number(hoursAgo),
      });
      if (ok) {
        setNewHandle('');
        setNewBody('');
      }
      return; // the thread appears via the worker + /ws, like a real DM
    }
    const id = seedInboundDm({
      handle: newHandle.trim(),
      studioId: (selected?.primaryStudioId ?? defaultStudio) as StudioId,
      body: newBody.trim() || undefined,
    });
    setNewHandle('');
    setNewBody('');
    setSelectedId(id);
  };

  const webReady = web.name.trim() && web.mobile.trim() && web.message.trim() && webState !== 'sending';
  const sendWebEnquiry = async () => {
    if (!webReady) return;
    setWebState('sending');
    setWebError(null);
    try {
      const res = await enquiryIntakeIntent({
        studio: defaultStudio,
        name: web.name.trim(),
        mobile: web.mobile.trim(),
        ...(web.email.trim() ? { email: web.email.trim() } : {}),
        message: web.message.trim(),
        ...(web.preferredDate ? { preferredDate: web.preferredDate } : {}),
        submissionId: `sim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        nowIso: clockIso,
      });
      if (!res.ok) throw new Error(res.message ?? res.error ?? `Refused (${res.status})`);
      setWeb({ name: '', mobile: '', email: '', message: '', preferredDate: '' });
      setWebState('sent');
    } catch (err) {
      setWebError(err instanceof Error ? err.message : String(err));
      setWebState('idle');
    }
  };
  const webInput = 'w-full bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-sm text-cream';

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4" data-testid="customer-simulator">
      {/* Left: thread list + new-conversation */}
      <aside className="panel p-3 space-y-2">
        <div className="space-y-1 border-b border-gold-dim/20 pb-3 mb-2">
          <p className="label-small-caps text-gold-bright">New conversation</p>
          <input
            data-testid="sim-new-handle"
            value={newHandle}
            onChange={(e) => setNewHandle(e.target.value)}
            placeholder="instagram handle"
            className="w-full bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-sm text-cream"
          />
          <input
            data-testid="sim-new-body"
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            placeholder="first message (optional)"
            className="w-full bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-sm text-cream"
          />
          {devHarness ? (
            <>
              <label className="flex items-center gap-2 text-xs text-cream-soft">
                <input
                  type="checkbox"
                  data-testid="sim-new-hookchat"
                  checked={viaHookchat}
                  onChange={(e) => setViaHookchat(e.target.checked)}
                />
                Via HookChat pipeline
              </label>
              {viaHookchat ? (
                <select
                  data-testid="sim-new-hoursago"
                  value={hoursAgo}
                  onChange={(e) => setHoursAgo(e.target.value)}
                  className="w-full bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-sm text-cream"
                >
                  <option value="0">Just now</option>
                  <option value="30">30 hours ago (agent window)</option>
                  <option value="200">8 days ago (closed)</option>
                </select>
              ) : null}
            </>
          ) : null}
          <button type="button" data-testid="sim-new-send" onClick={() => void createConversation()} className="btn-quiet text-xs w-full">
            + Start as customer
          </button>
          {error ? <p data-testid="sim-error" className="text-xs text-orange">{error}</p> : null}
        </div>
        <div className="space-y-1 border-b border-gold-dim/20 pb-3 mb-2" data-testid="sim-web-form">
          <p className="label-small-caps text-gold-bright">Website form (email)</p>
          <p className="text-[11px] text-cream-soft">Goes straight to the Agent&apos;s list, not this inbox.</p>
          <input data-testid="sim-web-name" value={web.name} onChange={(e) => setWeb({ ...web, name: e.target.value })} placeholder="name" className={webInput} />
          <input data-testid="sim-web-mobile" value={web.mobile} onChange={(e) => setWeb({ ...web, mobile: e.target.value })} placeholder="mobile" className={webInput} />
          <input data-testid="sim-web-email" value={web.email} onChange={(e) => setWeb({ ...web, email: e.target.value })} placeholder="email (optional)" className={webInput} />
          <textarea data-testid="sim-web-message" value={web.message} onChange={(e) => setWeb({ ...web, message: e.target.value })} placeholder="message" rows={2} className={webInput} />
          <input data-testid="sim-web-date" type="date" value={web.preferredDate} onChange={(e) => setWeb({ ...web, preferredDate: e.target.value })} aria-label="preferred date (optional)" className={webInput} />
          <button type="button" data-testid="sim-web-send" disabled={!webReady} onClick={() => void sendWebEnquiry()} className="btn-quiet text-xs w-full disabled:opacity-40">
            {webState === 'sending' ? 'Sending…' : 'Send enquiry'}
          </button>
          {webState === 'sent' ? <p data-testid="sim-web-sent" className="text-xs text-mint">Sent. It&apos;s on the Agent&apos;s list.</p> : null}
          {webError ? <p data-testid="sim-web-error" className="text-xs text-orange">{webError}</p> : null}
        </div>
        {feed.map((i) => (
          <button
            key={i.customerId}
            type="button"
            data-testid={`sim-thread-${i.customerId}`}
            onClick={() => setSelectedId(i.customerId)}
            className={
              i.customerId === selectedId
                ? 'w-full text-left rounded px-2 py-2 border border-gold-bright bg-gold/10'
                : 'w-full text-left rounded px-2 py-2 border border-gold-dim/20 hover:border-gold-bright/50'
            }
          >
            <span className="block text-sm text-cream">{i.customer.firstName} {i.customer.lastName}</span>
            <span className="block text-xs text-cream-soft truncate">{i.preview}</span>
            {i.awaitingResponse ? (
              <span className="text-[10px] uppercase tracking-widest text-orange">awaiting reply</span>
            ) : null}
          </button>
        ))}
        {feed.length === 0 ? <p className="font-serif text-cream-soft text-sm">No customer messages yet.</p> : null}
      </aside>

      {/* Right: thread + customer action bar */}
      <section className="panel p-4">
        {!selected ? (
          <p className="font-serif text-cream-soft">Pick a thread, or start a new conversation.</p>
        ) : (
          <>
            <ConversationThread customer={selected} />
            <div className="mt-4 border-t border-gold-dim/20 pt-4 space-y-3" data-testid="sim-action-bar">
              <div className="flex gap-2">
                <input
                  data-testid="sim-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="reply as the customer…"
                  disabled={blockHookchatReply}
                  className="flex-1 bg-ink-deep border border-gold-dim/40 rounded px-3 py-2 text-sm text-cream disabled:opacity-50"
                />
                <button
                  type="button"
                  data-testid="sim-reply-send"
                  onClick={() => void sendReply()}
                  disabled={blockHookchatReply}
                  className="btn-quiet text-xs disabled:opacity-50"
                >
                  Send
                </button>
              </div>
              {blockHookchatReply ? (
                <p className="font-serif text-cream-soft text-xs">
                  Real Instagram thread — the customer replies from Instagram.
                </p>
              ) : null}
              {link ? (
                <a
                  data-testid="sim-open-link"
                  href={`/customer-book/${link.token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-xs inline-block"
                >
                  Open customer link ↗
                </a>
              ) : pending ? (
                <div className="space-y-2">
                  <a
                    data-testid="sim-open-deposit"
                    href={`/customer-deposit/${pending.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-xs inline-block"
                  >
                    Open customer link ↗
                  </a>
                  <div className="flex gap-2">
                    <button type="button" data-testid="sim-view-deposit" onClick={() => simulateDepositLinkViewed(pending.id)} className="btn-quiet text-xs">
                      Mark viewed
                    </button>
                    {selected && !emailOnFile(selected) ? (
                      <input
                        data-testid="sim-pay-email"
                        type="email"
                        value={payEmail}
                        onChange={(e) => setPayEmail(e.target.value)}
                        placeholder="customer's email"
                        className="bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-xs text-cream"
                      />
                    ) : null}
                    <button
                      type="button"
                      data-testid="sim-pay-deposit"
                      disabled={!depositEmailFor(selected, payEmail)}
                      onClick={() => {
                        const contactEmail = depositEmailFor(selected, payEmail);
                        simulateStripeWebhookPaid({ depositId: pending.id, ...(contactEmail ? { contactEmail } : {}) });
                        setPayEmail('');
                      }}
                      className="btn-primary text-xs disabled:opacity-40"
                    >
                      Pay deposit
                    </button>
                  </div>
                </div>
              ) : (
                <p className="font-serif text-cream-soft text-xs">No link sent yet — send one from the agent/messenger side first.</p>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
